<template>
  <div class="staff-credits-kpi-cards">
    <!-- Live visibility, not an accounting entry: open_credits_micro/1e6 * peg_usd. -->
    <stats-card
      data-testid="kpi-liability"
      :value="liabilityUsdDisplay"
      :label="t('Passivo de créditos')"
      icon="pi pi-shield"
      variant="warning"
    />

    <!-- Measured: SUM(provider_cost_usd) over settle+meter rows in period. -->
    <div class="kpi-tile" data-testid="kpi-cogs-realized">
      <stats-card
        :value="cogsRealizedUsdDisplay"
        :label="t('COGS realizado')"
        icon="pi pi-credit-card"
        variant="danger"
      />
      <p
        v-if="dados.cost_rows_missing_cost > 0"
        class="kpi-caveat"
        data-testid="kpi-cogs-realized-caveat"
      >
        {{ cogsMissingCostCaveat }}
      </p>
    </div>

    <!-- Multi-currency: rendered per currency, never converted into one invented total. -->
    <stats-card
      data-testid="kpi-cash-received"
      :value="cashReceivedDisplay"
      :label="t('Caixa recebido')"
      icon="pi pi-wallet"
      variant="success"
    />

    <!-- Provisioning cost of the credits SOLD in the period, at the current peg.
         Paired with the per-currency cash tile above; see the computed's docblock
         for why this is not a single combined margin figure. -->
    <div class="kpi-tile" data-testid="kpi-cost-of-sold">
      <stats-card
        :value="costOfCreditsSoldDisplay"
        :label="t('Custo dos créditos vendidos (USD)')"
        icon="pi pi-tag"
        variant="info"
      />
      <p class="kpi-caveat">
        {{ t('Créditos vendidos × peg atual. Compare com o caixa recebido acima: a margem num único número exigiria uma taxa de câmbio de exibição, ainda não definida.') }}
      </p>
    </div>

    <!-- Free grants with no matching revenue: granted_free_micro/1e6 * peg_usd. -->
    <stats-card
      data-testid="kpi-liability-no-cash"
      :value="liabilityNoCashUsdDisplay"
      :label="t('Passivo sem caixa')"
      icon="pi pi-gift"
      variant="warning"
    />

    <!-- Full/Master platform spend — its OWN line, never folded into COGS or margin. -->
    <stats-card
      data-testid="kpi-metered"
      :value="meteredDisplay"
      :label="t('Consumo não faturado')"
      icon="pi pi-server"
      variant="primary"
    />

    <!-- Provider coverage — can we honour what we sold? coverage = provider
         account balance (USD) − liability_usd. Negative is unmistakable
         (danger variant + explicit alert line): it means credits were sold
         beyond what the provider account can fund. Fail-soft: the agent's
         /provider/balance never throws (200 + {available:false,reason} on any
         upstream trouble), so this tile always renders SOMETHING honest —
         never 0, never a stale number passed off as fresh. Whole-account
         scope (shared across environments, not this franchise) is always
         named. No invented FX: a non-USD-only balance is shown in its real
         currency with an explicit "cannot compute" note (§2.6 allows only a
         labelled display rate, and none exists yet). -->
    <div class="kpi-tile" data-testid="kpi-provider-coverage">
      <stats-card
        :value="providerCoverageValueDisplay"
        :label="t('Cobertura do provedor (DeepSeek)')"
        icon="pi pi-database"
        :variant="providerCoverageVariant"
      />
      <p
        v-if="providerCoverageState.kind === 'ok' && providerCoverageState.coverageUsd < 0"
        class="kpi-caveat kpi-caveat-danger"
        data-testid="kpi-provider-coverage-negative"
      >
        {{ t('SALDO NEGATIVO: foram vendidos mais créditos do que a conta do provedor pode cobrir agora.') }}
      </p>
      <p
        v-if="providerCoverageState.kind === 'unavailable'"
        class="kpi-caveat"
        data-testid="kpi-provider-coverage-unavailable"
      >
        {{ t('Saldo do provedor indisponível:') }} {{ providerCoverageState.reason }}
      </p>
      <p
        v-if="providerCoverageState.kind === 'no-usd'"
        class="kpi-caveat"
        data-testid="kpi-provider-coverage-no-fx"
      >
        {{ t('Sem saldo em USD na conta do provedor — não é possível calcular a cobertura sem uma taxa de câmbio de exibição rotulada.') }}
      </p>
      <p
        v-if="providerCoverageState.kind === 'ok' || providerCoverageState.kind === 'no-usd'"
        class="kpi-caveat"
        data-testid="kpi-provider-coverage-scope"
      >
        {{ t('Saldo da conta INTEIRA do provedor, compartilhada entre ambientes — não é exclusivo desta franquia.') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import type { AiCredits_AdminOverviewResponseDados } from '../../../contracts/AiCredits_AdminOverviewResponse';
import StatsCard from '../../../ui/StatsCard.vue';
import { formatCredits } from '../../assistant/formatCredits';
import { currencyFactor } from '../../../utils/currencyMinorUnits';
import { useProviderBalance } from '../../../composables/useProviderBalance';
import type { ProviderBalanceInfo } from '../../../composables/aiAgentContract';

const { t, n } = useI18n();

interface Props {
  dados: AiCredits_AdminOverviewResponseDados;
}

const props = defineProps<Props>();

/** Format a USD decimal-string field (e.g. '12.34500000') as USD currency, regardless of locale. */
function usd(value: string): string {
  return n(parseFloat(value) || 0, { style: 'currency', currency: 'USD' });
}

/** Format one currency amount, falling back to a plain decimal if the currency code is unusable. */
function formatCurrencyAmount(amountMajor: number, currency: string): string {
  try {
    return n(amountMajor, { style: 'currency', currency });
  } catch {
    return `${amountMajor.toFixed(2)} ${currency}`;
  }
}

const liabilityUsdDisplay = computed(() => usd(props.dados.liability_usd));
const cogsRealizedUsdDisplay = computed(() => usd(props.dados.cogs_realized_usd));
const liabilityNoCashUsdDisplay = computed(() => usd(props.dados.liability_no_cash_usd));

const cogsMissingCostCaveat = computed(() =>
  `${t('Aviso:')} ${props.dados.cost_rows_missing_cost} ` +
  t('linha(s) do período sem custo de provedor registrado — COGS realizado está subestimado.'),
);

/** metered_* is its own line: the USD figure plus the humanized credit count — never COGS/margin. */
const meteredDisplay = computed(() => {
  const credits = formatCredits(props.dados.metered_micro);
  return `${usd(props.dados.metered_usd)} (${credits} ${t('cr')})`;
});

/**
 * Per-currency breakdown, each row in its OWN currency — no FX invented to
 * force a single total. amount_cents is converted to major units via the
 * currency's real minor-unit factor (BRL/USD → /100, CLP → /1).
 */
const cashReceivedDisplay = computed(() => {
  if (props.dados.cash_received.length === 0) {
    return t('Nenhum valor recebido no período');
  }
  return props.dados.cash_received
    .map((row) => {
      const major = row.amount_cents / currencyFactor('pt-BR', row.currency);
      return formatCurrencyAmount(major, row.currency);
    })
    .join(' · ');
});

/**
 * Provisioning cost of the credits SOLD in the period: paid grants
 * (total granted minus free grants) valued at the CURRENT peg. Peg-based rather
 * than cogs_realized_usd on purpose — "sold" and "consumed" are different
 * populations of credits; this is the cost of what was SOLD, not what was BURNED.
 *
 * This deliberately REPLACES an earlier single "gross margin on sale" tile. That
 * tile subtracted the all-currency cost from USD-ONLY revenue, so on a BRL-only
 * tenant (New Shoes: every purchase is BRL) the USD revenue was 0 and a
 * profitable period rendered as a NEGATIVE dollar figure. Combining revenue
 * across BRL/USD/CLP into one number requires an FX rate, and §2.6/D-o allows
 * that only as an explicitly LABELLED display rate — there is no such rate
 * defined in this domain yet (fx_rate_usd_brl is dormant and M6 drops it, and it
 * has no CLP leg at all). Rather than invent one or silently discard most of the
 * revenue, we show the honest halves: cash received per currency (tile above)
 * and the USD cost of what was sold (this tile). The single margin figure waits
 * for a real display-rate decision.
 */
const costOfCreditsSoldDisplay = computed(() => {
  const creditsSoldMicro = props.dados.total_granted_micro - props.dados.granted_free_micro;
  const pegUsd = parseFloat(props.dados.peg_usd) || 0;
  return n((creditsSoldMicro / 1_000_000) * pegUsd, { style: 'currency', currency: 'USD' });
});

// ─── Provider coverage tile ────────────────────────────────────────────────
// coverage = provider account balance (USD) − liability_usd. The balance
// comes straight from the agent's own composable (network access lives ONLY
// in useProviderBalance, per CLAUDE.md rule 3/5) — this component only reads
// its reactive state and does the display-layer arithmetic/formatting.
const { balance: providerBalance, loading: providerBalanceLoading, error: providerBalanceError, load: loadProviderBalance } = useProviderBalance();

onMounted(async () => {
  try {
    await loadProviderBalance();
  } catch {
    // The composable already set `error`; matches the AgentStatsPanel /
    // AgentHealthPanel convention so a load rejection never propagates as
    // unhandled.
  }
});

/** PT label for the agent's fail-soft reason codes (never a raw English leak). */
function providerBalanceReasonLabel(reason: string): string {
  switch (reason) {
    case 'provider_unreachable':
      return t('não foi possível conectar ao provedor');
    case 'provider_error':
      return t('o provedor retornou um erro');
    case 'invalid_response':
      return t('o provedor retornou uma resposta inválida');
    default:
      return reason;
  }
}

type ProviderCoverageState =
  | { kind: 'loading' }
  | { kind: 'unavailable'; reason: string }
  | { kind: 'no-usd'; entries: ProviderBalanceInfo[] }
  | { kind: 'ok'; coverageUsd: number };

/**
 * Discriminated state for the tile: loading → composable transport error
 * (401/403/network, distinct from the agent's own fail-soft shape) → the
 * agent's fail-soft `{available:false,reason}` → a balance with no USD entry
 * (coverage cannot be computed without an invented FX rate) → a real coverage
 * number (possibly negative).
 */
const providerCoverageState = computed((): ProviderCoverageState => {
  if (providerBalanceError.value) {
    return { kind: 'unavailable', reason: providerBalanceError.value };
  }
  const balance = providerBalance.value;
  if (balance === null) {
    return { kind: 'loading' };
  }
  if (!balance.available) {
    return { kind: 'unavailable', reason: providerBalanceReasonLabel(balance.reason) };
  }
  const usdEntry = balance.balance_infos.find((entry) => entry.currency === 'USD');
  if (!usdEntry) {
    return { kind: 'no-usd', entries: balance.balance_infos };
  }
  const balanceUsd = parseFloat(usdEntry.total_balance) || 0;
  const liabilityUsd = parseFloat(props.dados.liability_usd) || 0;
  return { kind: 'ok', coverageUsd: balanceUsd - liabilityUsd };
});

const providerCoverageValueDisplay = computed(() => {
  const state = providerCoverageState.value;
  if (state.kind === 'loading') {
    return t('Carregando…');
  }
  if (state.kind === 'unavailable') {
    return t('Indisponível');
  }
  if (state.kind === 'no-usd') {
    if (state.entries.length === 0) {
      return t('Sem saldo informado');
    }
    return state.entries
      .map((entry) => formatCurrencyAmount(parseFloat(entry.total_balance) || 0, entry.currency))
      .join(' · ');
  }
  return n(state.coverageUsd, { style: 'currency', currency: 'USD' });
});

const providerCoverageVariant = computed(() => {
  const state = providerCoverageState.value;
  if (state.kind === 'ok') {
    return state.coverageUsd < 0 ? 'danger' : 'success';
  }
  if (state.kind === 'unavailable' || state.kind === 'no-usd') {
    return 'warning';
  }
  return 'info';
});
</script>

<style scoped>
.staff-credits-kpi-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 30px;
}

.staff-credits-kpi-cards > * {
  flex: 1 1 calc(33% - 14px);
  min-width: 250px;
}

.kpi-tile {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.kpi-caveat {
  margin: 0;
  font-size: 12px;
  color: var(--p-text-color-secondary);
}
</style>
